import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { smoothlyMenu } from 'app/app.helpers';
import { Guid } from 'app/core/data/entities';
import { DealerEntryAttachmentModel } from 'app/core/model/dealer-entry-attachment-model';
import { DealerEntryModel } from 'app/core/model/dealer-entry-model';
import { alert, confirm } from 'devextreme/ui/dialog';
import validationEngine from 'devextreme/ui/validation_engine';
import { environment } from 'environments/environment';
import { AppInformationService, PortalService } from '../../core/core.module';
import { StateTaxExemption } from 'app/core/model/state-tax-exemption';
declare var jQuery: any;

@Component({
    selector: 'app-dealer-entry-portal',
    templateUrl: './dealer-entry-portal.component.html',
    styleUrls: ['./dealer-entry-portal.component.scss'],
    standalone: false
})
export class DealerEntryPortalComponent implements OnInit {
    code: Guid;
    dealerEntry: DealerEntryModel;
    attachments: DealerEntryAttachmentModel[];
    stateTaxExemptions: StateTaxExemption[];
    areAttachmentsValid = true;

    uploadUrl = `${environment.serviceName}/file/saveDealerEntryAttachments`;
    validationGroup = 'dealerPortalValidationGroup';

    constructor(private route: ActivatedRoute, protected location: Location, private appInfo: AppInformationService, private portalService: PortalService) {}

    async ngOnInit() {
        this.toggleNavigation();
        this.code = this.route.snapshot.params['code'];
        if (!this.code) return;

        await this.getData();
        if (!this.dealerEntry) return;

        this.stateTaxExemptions = await this.portalService.getStateTaxExemptions();

        await this.portalService.setDealerEntryViewed(this.code);
    }

    async getData() {
        this.dealerEntry = await this.portalService.getDealerEntry(this.code);
        await this.getAttachments();
    }

    async getAttachments() {
        this.attachments = await this.portalService.getDealerEntryAttachments(this.code);
    }

    toggleNavigation(): void {
        jQuery('body').toggleClass('mini-navbar');
        smoothlyMenu();
    }

    async submit() {
        const isValid = this.validate();
        if (!isValid) return;

        if (!this.attachments || this.attachments.length === 0) {
            this.areAttachmentsValid = false;
            await alert('At least one attachment is required.', this.appInfo.settings.company);
            return;
        }
        this.areAttachmentsValid = true;

        const success = await confirm('Are you sure you want to submit? You will no longer be able to edit this dealer form after submitting.', this.appInfo.settings.company);
        if (!success) return;

        const response = await this.portalService.submitDealerEntry(this.dealerEntry);
        console.log(response);
        if (response) {
            await alert('The dealer form has been submitted.', this.appInfo.settings.company);
            this.portalService.sendNewDealerAlert(this.code);
        } else {
            await alert('Failed to submit the dealer form. Please refresh and try again.', this.appInfo.settings.company);
        }

        await this.getData();
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        if (groupConfig) {
            const result = validationEngine.validateGroup(this.validationGroup);
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before submitting.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    uploadValueChanged(e) {
        const url = e.component.option('uploadUrl');
        e.component.option('uploadUrl', `${url}?code=${this.code}`);
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    async uploaded(e) {
        this.appInfo.pleaseWaitHide();
        if (e?.request?.responseText) {
            const success = JSON.parse(e.request.responseText) as boolean;
            if (!success) await this.showUploadError();
            else this.areAttachmentsValid = true;
        }
        await this.getAttachments();
    }

    async uploadError(e) {
        this.appInfo.pleaseWaitHide();
        await this.showUploadError();
    }

    async showUploadError() {
        await alert('An error occurred while attempting to upload the file(s). Please refresh and try again.', this.appInfo.settings.company);
    }

    async removeAttachment(e: any, attachment: DealerEntryAttachmentModel) {
        e.preventDefault();
        const success = await this.portalService.deleteDealerEntryAttachment(attachment.id, this.code);
        if (!success) await alert('An error occurred while attempting to delete the file. Please refresh and try again.', this.appInfo.settings.company);
        await this.getAttachments();
    }

    openAttachment(attachment: DealerEntryAttachmentModel) {
        const dealerUrl = 'https://peccerium.blob.core.windows.net/genesis-trailers/dealer/attachments/';
        let fileName = attachment.fileName.replace(dealerUrl, '');
        const fileNameURI = encodeURIComponent(fileName);
        const path = dealerUrl + fileNameURI;
        window.open(path, '_blank');
    }

    openStateTaxExemption(exemption: StateTaxExemption) {
        window.open(exemption.url, '_blank');
    }

    async openNewDealerPdf() {
        const url = await this.portalService.getNewDealerPdf();
        window.open(url, '_blank');
    }
}
